<template>
  <v-container fluid>
    <v-row>
      <v-col
        sm="12"
        md="4"
        lg="3"
        xl="2"
      >
        <v-row>
          <v-col>
            <player-container
              :disabled="playerIsDisabled"
              @playerDefined="setPlayer"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <project-container
              :disabled="projectIsDisabled"
              @projectDefined="setProject"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <period-container
              :disabled="!playerIsDisabled && !projectIsDisabled"
              @periodDefined="setPeriod"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col>
        <v-row>
          <v-col
            v-if="barSeries.length > 0"
            cols="6"
          >
            <apexchart
              :options="barOptions"
              :series="barSeries"
              height="350"
            />
          </v-col>
          <v-col
            v-if="timelineSeries.length > 0"
            cols="6"
          >
            <apexchart
              :options="timelineOptions"
              :series="timelineSeries"
              height="350"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="lineSeries.length > 0"
            cols="6"
          >
            <apexchart
              :options="lineOptions"
              :series="lineSeries"
              height="350"
            />
          </v-col>
          <v-col
            v-if="pieSeries.length > 0"
            cols="6"
          >
            <apexchart
              :options="pieOptions"
              :series="pieSeries"
              height="350"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Components
import periodContainer from "./StatsPage/PeriodContainer";
import playerContainer from "./StatsPage/PlayerContainer";
import projectContainer from "./StatsPage/ProjectContainer";

// Charts
import apexchart from "vue-apexcharts";

// Libs
const moment = require("moment");
const momentDurationFormatSetup = require("moment-duration-format");

// Connectors
import { StatsConnector } from "./Database/StatsConnector";

export default {
  components: {
    periodContainer,
    playerContainer,
    projectContainer,
    apexchart,
  },
  data() {
    return {
      statsConnector: new StatsConnector(this.$store.state.user),
      currentPeriod: {
        start: new Date().toISOString().substr(0, 10),
        end: new Date().toISOString().substr(0, 10),
      },
      currentPlayer: null,
      currentProject: null,
      barOptions: {
        chart: {
          type: "bar",
        },
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          bar: {
            distributed: true,
          },
        },
        theme: {
          mode: this.$vuetify.theme.dark ? "dark" : "light",
          palette: "palette1",
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return moment.duration(value, "seconds").format("hh[h]mm[m]ss[s]");
            },
          },
        },
        yaxis: {
          show: false,
        },
      },
      lineOptions: {
        chart: {
          type: "line",
        },
        stroke: {
          curve: "smooth",
        },
        theme: {
          mode: this.$vuetify.theme.dark ? "dark" : "light",
          palette: "palette2",
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return moment.duration(value, "seconds").format("hh[h]mm[m]ss[s]");
            },
          },
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          show: false,
        },
      },
      pieOptions: {
        chart: {
          type: "donut",
        },
        labels: [],
        legend: {
          position: "right",
        },
        theme: {
          mode: this.$vuetify.theme.dark ? "dark" : "light",
          palette: "palette1",
        },
        tooltip: {
          enabled: false,
        },
      },
      timelineOptions: {
        chart: {
          type: "rangeBar",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
          },
        },
        theme: {
          mode: this.$vuetify.theme.dark ? "dark" : "light",
          palette: "palette1",
        },
        xaxis: {
          type: "datetime",
        },
      },
      barSeries: [],
      lineSeries: [],
      pieSeries: [],
      timelineSeries: [],
    };
  },
  computed: {
    playerIsDisabled() {
      return this.currentProject !== null;
    },
    projectIsDisabled() {
      return this.currentPlayer !== null;
    },
  },
  created() {
    document.title = this.$t("navbar.stats") + " · " + this.$t("appname");
    this.$store.dispatch("setHeader", this.$t("navbar.stats"));
  },
  mounted() {
    momentDurationFormatSetup(moment);
  },
  methods: {
    createProjectStats() {
      this.resetCharts();

      this.statsConnector
        .playtimeProject(this.currentProject, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            let totalTime = 0;

            const values = response.map((row) => {
              totalTime += row.count_time;

              return {
                x: row.player_name,
                y: row.count_time,
              };
            });

            if (totalTime > 0) {
              this.pieSeries = response.map((row) => {
                this.pieOptions.labels.push(row.player_name);
                return row.count_time / totalTime;
              });
            }

            this.barSeries = [
              {
                name: this.$t("stats.playtime"),
                data: values,
              },
            ];
          }
        })
        .catch((err) => {
          console.error(err);
        });

      this.statsConnector
        .timelineProject(this.currentProject, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            const values = response.map((row) => {
              return {
                x: row.player_name,
                y: [new Date(row.first_play).getTime(), new Date(row.last_play).getTime()],
              };
            });

            this.timelineSeries = [
              {
                data: values,
              },
            ];
          }
        })
        .catch((err) => {
          console.error(err);
        });

      this.statsConnector
        .daysProject(this.currentProject, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            const values = response.map((row) => {
              const day = this.$t("calendar.days-array")[row.days_play];
              this.lineOptions.xaxis.categories.push(day);
              return row.sum_play;
            });

            this.lineSeries = [
              {
                name: this.$t("stats.playtime"),
                data: values,
              },
            ];
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    createPlayerStats() {
      this.resetCharts();

      this.statsConnector
        .playtimePlayer(this.currentPlayer, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            let totalTime = 0;

            const values = response.map((row) => {
              totalTime += row.count_time;

              return {
                x: row.project_name,
                y: row.count_time,
              };
            });

            if (totalTime > 0) {
              this.pieSeries = response.map((row) => {
                this.pieOptions.labels.push(row.project_name);
                return row.count_time / totalTime;
              });
            }

            this.barSeries = [
              {
                name: this.$t("stats.playtime"),
                data: values,
              },
            ];
          } else {
            this.barSeries = [];
          }
        })
        .catch((err) => {
          console.error(err);
        });

      this.statsConnector
        .timelinePlayer(this.currentPlayer, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            const values = response.map((row) => {
              return {
                x: row.project_name,
                y: [new Date(row.first_play).getTime(), new Date(row.last_play).getTime()],
              };
            });

            this.timelineSeries = [
              {
                data: values,
              },
            ];
          }
        })
        .catch((err) => {
          console.error(err);
        });

      this.statsConnector
        .daysPlayer(this.currentPlayer, this.currentPeriod)
        .then((response) => {
          if (response.length > 0) {
            const values = response.map((row) => {
              const day = this.$t("calendar.days-array")[row.days_play];
              this.lineOptions.xaxis.categories.push(day);
              return row.sum_play;
            });

            this.lineSeries = [
              {
                name: this.$t("stats.playtime"),
                data: values,
              },
            ];
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    resetCharts() {
      this.barSeries = [];
      this.pieSeries = [];
      this.timelineSeries = [];
      this.lineSeries = [];
      this.lineOptions.xaxis.categories = [];
      this.pieOptions.labels = [];
    },
    setPeriod(val) {
      this.currentPeriod = val;

      if (this.playerIsDisabled) {
        this.createProjectStats();
      } else if (this.projectIsDisabled) {
        this.createPlayerStats();
      }
    },
    setPlayer(val) {
      this.currentProject = null;
      this.currentPlayer = val;
    },
    setProject(val) {
      this.currentPlayer = null;
      this.currentProject = val;
    },
  },
};
</script>

<style></style>
